import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar as CalendarIcon, Check, X, Mail, ArrowLeft, Copy, CheckIcon, FileText } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGoogle, faYahoo, faMicrosoft, faApple } from '@fortawesome/free-brands-svg-icons';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import { generateCalendarUrls } from '../lib/calendar';
import { useAuth } from '../contexts/AuthContext';
import { useMsal } from "@azure/msal-react";
import { createCalendarEvent, updateCalendarEvent, getGraphClient } from '../lib/msGraph';

interface Meeting {
  id: string;
  title: string;
  first_name: string;
  last_name: string;
  date: string;
  duration: number;
  location: string;
  confirmed: boolean | null;
  created_by: string;
  phone_number?: string;
  attendees?: string[];
  microsoft_event_id?: string;
  meeting_description?: string;
  meeting_category: string;
}

export default function MeetingConfirmation() {
  const { id } = useParams<{ id: string }>();
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [countdown, setCountdown] = useState<string>('');
  const [email, setEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [copied, setCopied] = useState(false);
  const { user } = useAuth();
  const { accounts } = useMsal();

  const meetingCategories = {
    phone: 'Telefon',
    video: 'Video-Call',
    inperson: 'persönlich vor Ort'
  };

  useEffect(() => {
    if (meeting) {
      document.title = `${meeting.title} | TOK rec2rec Terminplaner`;

      const description = `Termin: ${meeting.title} am ${new Date(meeting.date).toLocaleDateString('de-DE')} um ${new Date(meeting.date).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr`;
      
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', description);
      }

      const ogTitle = document.querySelector('meta[property="og:title"]');
      const ogDescription = document.querySelector('meta[property="og:description"]');
      if (ogTitle) {
        ogTitle.setAttribute('content', meeting.title);
      }
      if (ogDescription) {
        ogDescription.setAttribute('content', description);
      }

      const twitterTitle = document.querySelector('meta[property="twitter:title"]');
      const twitterDescription = document.querySelector('meta[property="twitter:description"]');
      if (twitterTitle) {
        twitterTitle.setAttribute('content', meeting.title);
      }
      if (twitterDescription) {
        twitterDescription.setAttribute('content', description);
      }
    }
  }, [meeting]);

  useEffect(() => {
    const fetchMeeting = async () => {
      try {
        const { data, error } = await supabase
          .from('meetings')
          .select('*')
          .eq('id', id)
          .single();

        if (error) throw error;
        setMeeting(data);
      } catch (error) {
        console.error('Error fetching meeting:', error);
        toast.error('Fehler beim Laden der Termindetails');
      } finally {
        setLoading(false);
      }
    };

    fetchMeeting();
  }, [id]);

  useEffect(() => {
    if (meeting) {
      const updateCountdown = () => {
        const now = new Date().getTime();
        const meetingTime = new Date(meeting.date).getTime();
        const distance = meetingTime - now;

        if (distance < 0) {
          setCountdown('Termin hat bereits stattgefunden');
          return;
        }

        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));

        let countdownText = '';
        if (days > 0) countdownText += `${days} Tag${days !== 1 ? 'e' : ''} `;
        if (hours > 0) countdownText += `${hours} Stunde${hours !== 1 ? 'n' : ''} `;
        if (minutes > 0) countdownText += `${minutes} Minute${minutes !== 1 ? 'n' : ''}`;

        setCountdown(countdownText.trim());
      };

      updateCountdown();
      const interval = setInterval(updateCountdown, 60000);
      return () => clearInterval(interval);
    }
  }, [meeting]);

  const trackCalendarClick = async (provider: string) => {
    try {
      await supabase
        .from('calendar_clicks')
        .insert([{ meeting_id: id, provider }]);
    } catch (error) {
      console.error('Error tracking calendar click:', error);
    }
  };

  const handleCalendarClick = (provider: string, url: string) => {
    trackCalendarClick(provider);
    window.open(url, '_blank');
  };

  const handleConfirm = async () => {
    if (!meeting) return;
    setConfirming(true);

    try {
      const { error: updateError } = await supabase
        .from('meetings')
        .update({ confirmed: true })
        .eq('id', id);

      if (updateError) throw updateError;

      toast.success('Termin erfolgreich bestätigt!');
      setMeeting({ ...meeting, confirmed: true });
    } catch (error) {
      console.error('Error confirming meeting:', error);
      toast.error('Fehler bei der Terminbestätigung');
    } finally {
      setConfirming(false);
    }
  };

  const handleDecline = async () => {
    if (!meeting) return;
    setConfirming(true);

    try {
      console.log('Declining meeting:', {
        meetingId: meeting.id,
        hasMicrosoftEventId: !!meeting.microsoft_event_id,
        hasMicrosoftAccount: !!accounts[0]
      });

      // If a Microsoft Event ID exists and a Microsoft account is connected,
      // delete the event from Microsoft Calendar
      if (meeting.microsoft_event_id && accounts[0]) {
        try {
          console.log('Attempting to delete Microsoft calendar event:', meeting.microsoft_event_id);
          const graphClient = getGraphClient();
          await graphClient
            .api(`/me/events/${meeting.microsoft_event_id}`)
            .delete();
          
          console.log('Microsoft calendar event deleted successfully');
          toast.success('Kalendereintrag wurde gelöscht');
        } catch (error) {
          console.error('Error deleting Microsoft calendar event:', error);
          toast.error('Fehler beim Löschen des Kalendereintrags');
        }
      }

      // Set the meeting to "declined"
      const { error } = await supabase
        .from('meetings')
        .update({ confirmed: false })
        .eq('id', id);

      if (error) throw error;

      toast.success('Termin abgelehnt');
      setMeeting({ ...meeting, confirmed: false });
    } catch (error) {
      console.error('Error declining meeting:', error);
      toast.error('Fehler beim Ablehnen des Termins');
    } finally {
      setConfirming(false);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!meeting || !email) return;

    setSendingEmail(true);
    try {
      // Store the email in calendar_emails table
      const { error: dbError } = await supabase
        .from('calendar_emails')
        .insert([{ meeting_id: id, email }]);

      if (dbError) throw dbError;

      // Update meeting with the new attendee
      const { error: updateError } = await supabase
        .from('meetings')
        .update({
          attendees: meeting.attendees ? [...meeting.attendees, email] : [email]
        })
        .eq('id', id);

      if (updateError) throw updateError;

      // If Microsoft account is connected, update the calendar event
      if (accounts[0]) {
        try {
          const startTime = new Date(meeting.date);
          const endTime = new Date(startTime.getTime() + meeting.duration * 60000);

          if (meeting.microsoft_event_id) {
            // Update existing event
            await updateCalendarEvent(meeting.microsoft_event_id, {
              attendees: [email]
            });
          } else {
            // Create new event
            const response = await createCalendarEvent({
              subject: meeting.title,
              start: startTime,
              end: endTime,
              location: meeting.location,
              description: `Termin mit ${meeting.first_name} ${meeting.last_name}${meeting.meeting_description ? `\n\n${meeting.meeting_description}` : ''}${meeting.phone_number ? `\nTelefon: ${meeting.phone_number}` : ''}`,
              attendees: [email]
            });

            // Store the Microsoft event ID
            await supabase
              .from('meetings')
              .update({ microsoft_event_id: response.id })
              .eq('id', id);
          }
        } catch (error) {
          console.error('Error updating Microsoft calendar event:', error);
          toast.error('Fehler beim Aktualisieren des Microsoft Kalendereintrags');
        }
      }

      toast.success('Kalendereinladung wurde per E-Mail gesendet');
      setEmail('');
      
      // Update local meeting state with new attendee
      setMeeting(prev => ({
        ...prev!,
        attendees: prev?.attendees ? [...prev.attendees, email] : [email]
      }));
    } catch (error) {
      console.error('Error sending calendar email:', error);
      toast.error('Fehler beim Senden der Kalendereinladung');
    } finally {
      setSendingEmail(false);
    }
  };

  const copyCurrentUrl = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      toast.success('URL in die Zwischenablage kopiert!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Fehler beim Kopieren der URL');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500" />
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">Termin nicht gefunden</h2>
          <p className="mt-2 text-gray-600">Der gesuchte Termin existiert nicht.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {user && (
        <div className="bg-green-50 border-b border-green-100">
          <div className="max-w-7xl mx-auto px-4 py-3 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center">
                <Link
                  to="/dashboard"
                  className="inline-flex items-center mr-4 text-green-600 hover:text-green-800"
                >
                  <ArrowLeft className="h-5 w-5 mr-1" />
                  Zurück
                </Link>
                <p className="text-sm text-green-700">
                  So sieht die Bestätigungsseite für Ihre Teilnehmer aus
                </p>
              </div>
              <button
                onClick={copyCurrentUrl}
                className="inline-flex items-center px-3 py-1 border border-green-300 rounded-md text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                {copied ? (
                  <CheckIcon className="h-4 w-4 mr-2" />
                ) : (
                  <Copy className="h-4 w-4 mr-2" />
                )}
                URL kopieren
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-md mb-8">
          <img
            src="https://www.tok-rec2rec.com/wp-content/uploads/2023/12/Logo-rec2rec.svg"
            alt="TOK rec2rec"
            className="h-16 mx-auto"
          />
        </div>

        <div className="bg-white rounded-lg shadow-lg border-2 border-gray-200 p-8 max-w-md w-full">
          <div className="text-center">
            <CalendarIcon className="mx-auto h-12 w-12 text-green-500" />
            <h2 className="mt-4 text-2xl font-bold text-gray-900">{meeting.title}</h2>
            <p className="mt-2 text-gray-600">
              mit {meeting.first_name} {meeting.last_name}
            </p>
            <p className="mt-2 text-gray-600">
              {new Date(meeting.date).toLocaleDateString('de-DE')} um{' '}
              {new Date(meeting.date).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr
              <span className="text-gray-500"> ({meeting.duration} min)</span>
            </p>
            {meeting.location && <p className="text-gray-600">Ort: {meeting.location}</p>}
            
            <div className="mt-2">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                {meetingCategories[meeting.meeting_category as keyof typeof meetingCategories]}
              </span>
            </div>

            {meeting.meeting_description && (
              <div className="mt-4 text-left bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center mb-2">
                  <FileText className="h-5 w-5 text-gray-500 mr-2" />
                  <h3 className="text-sm font-medium text-gray-900">Beschreibung</h3>
                </div>
                <p className="text-sm text-gray-600">{meeting.meeting_description}</p>
              </div>
            )}
            
            {countdown && (
              <p className="mt-4 text-sm font-medium text-green-600">
                Termin in: {countdown}
              </p>
            )}
          </div>

          {meeting.confirmed === null ? (
            <div className="mt-8 flex space-x-4">
              <button
                onClick={handleConfirm}
                disabled={confirming}
                className="flex-1 flex items-center justify-center px-4 py-2 border-2 border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
              >
                <Check className="w-5 h-5 mr-2" />
                Bestätigen
              </button>
              <button
                onClick={handleDecline}
                disabled={confirming}
                className="flex-1 flex items-center justify-center px-4 py-2 border-2 border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                <X className="w-5 h-5 mr-2" />
                Ablehnen
              </button>
            </div>
          ) : (
            <>
              <div className="mt-8 text-center">
                <div className={`inline-flex items-center px-4 py-2 rounded-md ${
                  meeting.confirmed 
                    ? 'bg-green-50 text-green-700'
                    : 'bg-red-50 text-red-700'
                }`}>
                  {meeting.confirmed ? (
                    <Check className="w-5 h-5 mr-2" />
                  ) : (
                    <X className="w-5 h-5 mr-2" />
                  )}
                  {meeting.confirmed ? 'Termin bestätigt' : 'Termin abgelehnt'}
                </div>
                {meeting.confirmed && (
                  <button
                    onClick={handleDecline}
                    className="mt-2 text-sm text-red-600 hover:text-red-800"
                  >
                    Termin absagen
                  </button>
                )}
              </div>

              {meeting.confirmed && (
                <div className="mt-8">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Zum Kalender hinzufügen</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {Object.entries(generateCalendarUrls({
                      title: meeting.title,
                      description: `Termin mit ${meeting.first_name} ${meeting.last_name}${meeting.meeting_description ? `\n\n${meeting.meeting_description}` : ''}${meeting.location ? `\nOrt: ${meeting.location}` : ''}`,
                      startTime: new Date(meeting.date),
                      endTime: new Date(new Date(meeting.date).getTime() + meeting.duration * 60000),
                      location: meeting.location || '',
                    })).map(([provider, url]) => {
                      const icon = {
                        google: faGoogle,
                        outlook: faMicrosoft,
                        yahoo: faYahoo,
                        ics: faApple
                      }[provider];
                      const label = {
                        google: 'Google Kalender',
                        outlook: 'Outlook',
                        yahoo: 'Yahoo Kalender',
                        ics: 'Apple Kalender'
                      }[provider];

                      return (
                        <button
                          key={provider}
                          onClick={() => handleCalendarClick(label, url)}
                          className="flex flex-col items-center p-4 border-2 border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <FontAwesomeIcon icon={icon} className="w-8 h-8 mb-2" />
                          <span className="text-sm text-gray-600">{label}</span>
                        </button>
                      );
                    })}
                  </div>

                  <div className="mt-6">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">
                      Kalendereinladung per E-Mail erhalten
                    </h4>
                    <form onSubmit={handleEmailSubmit} className="flex space-x-2">
                      <div className="flex-1">
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="E-Mail-Adresse eingeben"
                          className="w-full px-3 py-2 border-2 border-gray-300 rounded-md shadow-sm focus:border-green-500 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                          required
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={sendingEmail}
                        className="inline-flex items-center px-4 py-2 border-2 border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                      >
                        <Mail className="w-5 h-5 mr-2" />
                        Senden
                      </button>
                    </form>
                  </div>

                  {meeting.attendees && meeting.attendees.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">
                        Teilnehmer
                      </h4>
                      <div className="space-y-2">
                        {meeting.attendees.map((attendeeEmail, index) => (
                          <div
                            key={index}
                            className="flex items-center text-sm text-gray-600 bg-gray-50 p-2 rounded-md"
                          >
                            <Mail className="w-4 h-4 mr-2 text-gray-400" />
                            {attendeeEmail}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}